import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { getInitialLoanFields, ILLPALLRA, IVisibleProp } from 'config'
import { propertyStateChecklist } from 'config/propertyStateOptions'
import { convertNullToBlank, convertRange } from 'pages/Resources/GuidelineOverlay/logic'
import { useMemo, useState } from 'react'
import { Input2, Select2 } from 'stories/components'
import type { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import { getPrice3decimal, removeComma } from 'utils'

export type LlpaLlraMode = 'select' | 'range' | 'check'

const parseNumber = (value: string) => {
  const raw = String(value ?? '').replace(/,/g, '')
  if (raw === '' || Number.isNaN(Number(raw))) return NaN
  return removeComma(raw)
}

const normalizeFieldOptions = (options: any): ILLPALLRA[] => {
  if (!options) return []
  if (Array.isArray(options)) {
    return options
      .map((option) => {
        if (typeof option === 'string' || typeof option === 'number')
          return { key: String(option), title: String(option) }
        if (option && typeof option === 'object') {
          const title = String(option.title ?? option.value ?? option.key ?? '')
          const key = option.key !== undefined ? String(option.key) : title
          return title ? { key, title } : null
        }
        return null
      })
      .filter(Boolean) as ILLPALLRA[]
  }
  if (typeof options === 'object') {
    return Object.entries(options)
      .filter(([, title]) => title !== null && title !== undefined)
      .map(([key, title]) => ({ key, title: String(title) }))
  }
  return []
}

const sameRow = (row: ILLPALLRA, saved: ILLPALLRA) =>
  (!!row.key && saved.key === row.key) || (!!row.title && saved.title === row.title)

export const LlpaLlraSection = ({
  fieldKey,
  data,
  setData,
  mode,
  showError,
}: {
  fieldKey: string
  data: IVisibleProp
  setData: (data: IVisibleProp) => void
  mode: LlpaLlraMode
  showError: boolean
}) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const savedRows = (Array.isArray(data.llpaLLRAs) ? data.llpaLLRAs : []) as ILLPALLRA[]
  const fieldOptions = useMemo(() => {
    if (fieldKey === 'propertyState') {
      const checklist = propertyStateChecklist(data.options as ISelectOption[] | undefined)
      const savedKeys = new Set(savedRows.map((row) => row.key || row.title).filter(Boolean))
      return checklist
        .filter((opt) => opt.visible !== false || savedKeys.has(opt.key))
        .map((opt) => ({ key: opt.key, title: opt.title }))
    }
    return normalizeFieldOptions(
      Array.isArray(data.options) && data.options.length ? data.options : getInitialLoanFields()[fieldKey]?.options,
    )
  }, [fieldKey, data.options, savedRows])

  const rows = useMemo(() => {
    if (mode === 'check') {
      return ['Yes', 'No'].map((title) => savedRows.find((row) => row.title === title) || { title })
    }
    return savedRows
  }, [mode, savedRows])

  const optionChoices = (current?: ILLPALLRA) => {
    const used = new Set(savedRows.map((row) => row.key || row.title).filter(Boolean))
    const currentId = current?.key || current?.title
    return fieldOptions.reduce<Record<string, string>>((choices, option) => {
      const id = option.key || option.title
      if (!used.has(id) || id === currentId) choices[id] = option.title
      return choices
    }, {})
  }

  const saveRows = (nextRows: ILLPALLRA[]) => {
    const newData = cloneDeep(data)
    newData.llpaLLRAs = nextRows
    setData(newData)
  }

  const updateSavedRow = (row: ILLPALLRA, patch: Partial<ILLPALLRA>) => {
    const nextRows = cloneDeep(savedRows)
    const index = nextRows.findIndex((item) => sameRow(row, item))
    const nextRow = { ...(index >= 0 ? nextRows[index] : row), ...patch }
    if (index >= 0) nextRows[index] = nextRow
    else nextRows.push(nextRow)
    saveRows(nextRows)
  }

  const getDraftValue = (index: number, field: string, savedValue?: number) =>
    drafts[`${index}-${field}`] ?? convertNullToBlank(savedValue)

  const onDraft = (index: number, field: string, value: string) => {
    if (value !== '' && !/^-?\d*\.?\d*$/.test(value)) return
    setDrafts((current) => ({ ...current, [`${index}-${field}`]: value }))
  }

  const getRangeValue = (index: number, field: 'from' | 'to', savedValue?: number) => {
    const draft = drafts[`${index}-${field}`]
    if (draft !== undefined) return draft
    if (savedValue === undefined || Number.isNaN(Number(savedValue))) return ''
    return getPrice3decimal(savedValue)
  }

  const onRangeDraft = (index: number, field: 'from' | 'to', value: string) => {
    const raw = value.replace(/,/g, '')
    if (raw !== '' && raw !== '-' && !/^-?\d*\.?\d*$/.test(raw)) return
    setDrafts((current) => ({
      ...current,
      [`${index}-${field}`]: raw === '' || raw === '-' ? raw : getPrice3decimal(raw),
    }))
  }

  const onCommit = (index: number, field: 'LLPA' | 'LLRA' | 'from' | 'to', rawValue: string) => {
    const value = parseNumber(rawValue)
    const row = rows[index]
    const patch: Partial<ILLPALLRA> = { [field]: value }
    if (mode === 'range' && (field === 'from' || field === 'to')) {
      const from = field === 'from' ? value : row.from
      const to = field === 'to' ? value : row.to
      patch.from = from
      patch.to = to
      patch.title = convertRange({ min: from as number, max: to as number })
    }
    setDrafts((current) => {
      const nextDrafts = { ...current }
      delete nextDrafts[`${index}-${field}`]
      return nextDrafts
    })
    if (mode === 'check') {
      updateSavedRow(row, patch)
      return
    }
    const nextRows = cloneDeep(savedRows)
    nextRows[index] = { ...nextRows[index], ...patch }
    saveRows(nextRows)
  }

  const onChangeOption = (index: number, optionKey: string) => {
    const option = fieldOptions.find((item) => (item.key || item.title) === optionKey)
    if (!option) return
    const nextRows = cloneDeep(savedRows)
    nextRows[index] = { ...nextRows[index], key: option.key, title: option.title }
    saveRows(nextRows)
  }

  const onChangeTitle = (index: number, title: string) => {
    const nextRows = cloneDeep(savedRows)
    nextRows[index] = { ...nextRows[index], title }
    saveRows(nextRows)
  }

  const onAdd = () => {
    if (mode === 'select') saveRows([...savedRows, { title: '' }])
    if (mode === 'range') saveRows([...savedRows, { title: '', from: NaN, to: NaN }])
  }

  const onRemove = (index: number) => {
    saveRows(savedRows.filter((_, rowIndex) => rowIndex !== index))
    setDrafts({})
  }

  const description =
    mode === 'select'
      ? 'Add an option and set its LLPA and LLRA.'
      : mode === 'range'
      ? 'Add a from / to range and set its LLPA and LLRA.'
      : 'Set LLPA and LLRA for Yes and No.'

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold">LLPA / LLRA</p>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        {mode !== 'check' && (mode !== 'select' || !fieldOptions.length || savedRows.length < fieldOptions.length) && (
          <span className="btn-icon" onClick={onAdd}>
            <PlusIcon className="h-4 w-4" />
          </span>
        )}
      </div>

      {mode !== 'check' && !rows.length && (
        <div className="rounded-md border border-dashed border-gray-300 px-3 py-4 text-center text-sm text-gray-500">
          No adjustments yet. Use Add to create one.
        </div>
      )}

      {rows.map((row, index) => (
        <div
          key={`${mode}-${row.key || row.title || index}`}
          className={`grid items-start gap-2 ${mode === 'check' ? 'grid-cols-5' : 'grid-cols-6'}`}
        >
          {mode === 'range' ? (
            <>
              <Input2
                type="text"
                title="From"
                value={getRangeValue(index, 'from', row.from)}
                onChange={(value: string) => onRangeDraft(index, 'from', value)}
                onBlur={(value: string) => onCommit(index, 'from', value)}
              />
              <Input2
                type="text"
                title="To"
                value={getRangeValue(index, 'to', row.to)}
                onChange={(value: string) => onRangeDraft(index, 'to', value)}
                onBlur={(value: string) => onCommit(index, 'to', value)}
              />
            </>
          ) : mode === 'select' && fieldOptions.length ? (
            <div className="col-span-3">
              <Select2
                id={`llpa-option-${index}`}
                title="Option"
                options={optionChoices(row)}
                value={row.key || row.title || ''}
                hasDefaultOption
                defaultOptionText="Select"
                error={showError && !row.title ? 'This field is required.' : ''}
                onChange={(value: string) => onChangeOption(index, value)}
              />
            </div>
          ) : (
            <div className="col-span-3">
              <Input2
                type="text"
                title="Option"
                value={row.title}
                readOnly={mode === 'check'}
                onChange={(value: string) => onChangeTitle(index, value)}
                required={mode === 'select'}
                error={showError && mode === 'select' && !row.title ? 'This field is required.' : ''}
              />
            </div>
          )}
          <Input2
            type="text"
            title="LLPA"
            value={getDraftValue(index, 'LLPA', row.LLPA)}
            onChange={(value: string) => onDraft(index, 'LLPA', value)}
            onBlur={(value: string) => onCommit(index, 'LLPA', value)}
          />
          <Input2
            type="text"
            title="LLRA"
            value={getDraftValue(index, 'LLRA', row.LLRA)}
            onChange={(value: string) => onDraft(index, 'LLRA', value)}
            onBlur={(value: string) => onCommit(index, 'LLRA', value)}
          />
          {mode !== 'check' && (
            <button
              type="button"
              className="mt-7 flex h-8 w-8 items-center justify-center rounded text-red-500 hover:bg-red-50"
              onClick={() => onRemove(index)}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
